import axios from "axios";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { appState } from "../../AppState";
import ConnectriaTicketsService from "../../services/ConnectriaTicketsService";
import { getReadableDate } from "../../utility/DateUtil";
import useAsyncEffect from "../../utility/use-async-effect";
import { AdvancedTicketSearch } from "./AdvancedTicketSearch";
import {
  NewTabLink,
  TicketBladeContainer,
} from "./ConnectriaTicketsBlade";
import useTriaTicketsState, { createdBy } from "./ConnectriaTicketsState";
// reactstrap components
import { FormGroup, Input as ReactStrapInput, UncontrolledTooltip } from "reactstrap";
import { CSVUtility } from "../../utility/table-to-csv";
import { ClipboardCopyButton } from "../misc";

import momentLocalizer from "react-widgets-moment";
import "react-widgets/dist/css/react-widgets.css";
import "../../assets/scss/search/SearchBar.scss";
import { Button } from "../Marketing/Button/Button";
import { Input } from "../Marketing/Input/Input";
import PaginationControls from "../PaginationControls/PaginationControls";
import TicketService from "../../services/TicketService";
import TicketStatusBadge, { getTicketStatusClassName } from "./TicketStatusBadge";
import { PROVIDERS } from "./constants";

moment.locale("en");
momentLocalizer();
const momentTzGuess = moment.tz.guess();
const TICKETPULLSIZE = 25;
// The following status weights should match what's in tria-accounts-cdk ticketing-ingreration:
// TODO : Or we could just leave the default blank here and rely on the "tria-accounts-cdk ticketing-ingreration" to use its default.
const WEIGHTED_SORT =
  "case when status = 'WaitOnCust' then 1 " +
  "when status = 'New' then 2 " +
  "when status = 'Open' then 3 " +
  "when status = 'Scheduled' then 4 " +
  "when status = 'Resolved' then 5 " +
  "when status = 'Closed' then 6 " +
  "when status = 'Cancelled' then 7 " +
  "when status = 'Suspended' then 8 " +
  "else 99 end asc, lastUpdate desc";

// cti_enabled customer status filters: TODO reformat similar to HYBRID_STATUS_OPTIONS and .map() the <Input> where it's currently hardcoded.
const statusFilters = [
  [""], // All
  ["New", "Open", "Scheduled", "WaitOnCust"], // All Open
  ["New"],
  ["Open"],
  ["WaitOnCust"],
  ["Scheduled"],
  ["Resolved"],
  ["Closed"],
  ["Suspended"],
  ["Cancelled"],
];

// When the ticketing-integration Lambda function filters by the follwoing status values it ignores capitalization.
// An empty "status" array is treated as having all possible status values.
const HYBRID_STATUS_OPTIONS = [
  {
    "name": "All",
    "status": [],
  },
  {
    "name": "All Open",
    "status": [
      "AWAITING_CLIENT", // SN
      "WaitOnCust", // CT
      "New", // SN + CT
      "IN_PROGRESS", // SN
      "Open", // SN + CT
      "Scheduled", // CT
    ],
  },
  {
    "name": "New",
    "status": [
      "New", // SN + CT
    ],
  },
  {
    "name": "In Progress",
    "status": [
      "IN_PROGRESS", // SN
      "Open", // SN + CT
      "Scheduled", // CT
    ],
  },
  {
    "name": "Awaiting Customer",
    "status": [
      "WaitOnCust", // CT
      "AWAITING_CLIENT", // SN
    ],
  },
  {
    "name": "Closed",
    "status": [
      "Closed", // SN + CT
      "Resolved", // SN + CT
      "Cancelled", // SN + CT
      "Suspended", // CT
    ],
  },
];

function showItemBlade(ticketNo, ticketProvider, stateAppActions) {
  stateAppActions.setBlade({
    title: (
      <span>
        {"#" + ticketNo}{" "}
        <ClipboardCopyButton
          value={`${window.location.origin}/app/view/ticket/${ticketNo}`}
          tooltipMessage={"Copy ticket link"}
          target="copy-ticket-link"
          alertMessage={`Ticket #${ticketNo} link copied.`}
        />
        <NewTabLink
          link={`/app/view/ticket/${ticketNo}`}
          tooltipMessage={"Open In New Tab"}
          target="open-new-tab-link"
          alertMessage={""}
        />
      </span>
    ),
    visible: true,
    content: (
      <TicketBladeContainer id={ticketNo} ticketProvider={ticketProvider} />
    ),
  });
}

function InboxItems(props) {
  const items = props.tickets;
  const [isLoading, setIsLoading] = useState(true);
  const [stateApp] = appState();
  const [, setTypes] = useState();
  const [, setPriorities] = useState();
  const source = axios.CancelToken.source();
  useAsyncEffect(
    async (isMounted) => {
      try {
        setIsLoading(true);
        let resp = await ConnectriaTicketsService.ticketTypes({
          cancelToken: source.token,
        });
        setPriorities(ConnectriaTicketsService.ticketPriorities());
        if (!isMounted()) return;
        setTypes(resp);
        setIsLoading(false);
      } catch (error) {
        if (axios.isCancel(error)) {
          // request cancelled
        } else {
          throw error;
        }
      }
    },
    () => {
      source.cancel();
    },
    []
  );

  if (isLoading) {
    return null;
  } else {
    return (
      <>
        {items.map((item, i) => {
          const statusClassName = getTicketStatusClassName({
            provider: item.provider,
            status: item.status,
          });

          return (
            <button
              type="button"
              onClick={(e) =>
                e.target.className.includes("copy-clipboard")
                  ? null
                  : showItemBlade(item.id, item.provider, props.stateAppActions)
              }
              key={item.id}
              className={`mt-1 mb-1 list-group-item list-group-item-action pointer flex-column align-items-start ${statusClassName}`}
            >
              <div className="d-flex w-100 justify-content-between">
                <div className="row">
                  <h5 className="mb-1 d-block d-sm-none">#{item.id}</h5>
                  <h4 className="mb-1 d-none d-md-block">#{item.id}</h4>
                  <ClipboardCopyButton
                    className="ml-1 mt-1"
                    value={`${window.location.origin}/app/view/ticket/${item.id}`}
                    tooltipMessage={"Copy ticket link"}
                    alertMessage={`Ticket #${item.id} link copied.`}
                    idx={i}
                  />
                  <NewTabLink
                    itemId={item.id}
                    link={`/app/view/ticket/${item.id}`}
                    tooltipMessage={"Open In New Tab"}
                    alertMessage={""}
                  />
                </div>
                <small className="d-block d-sm-none pull-right">
                  Updated:{" "}
                  {getReadableDate(
                    item.lastUpdate,
                    moment(item.lastUpdate).tz(momentTzGuess).fromNow()
                  )}
                </small>
                <small className="d-block d-sm-none">
                  Created:{" "}
                  {getReadableDate(
                    item.created,
                    moment(item.created).tz(momentTzGuess).fromNow()
                  )}
                </small>
                <span className="pull-right d-none d-md-block">
                  Updated:{" "}
                  {getReadableDate(
                    item.lastUpdate,
                    moment(item.lastUpdate).tz(momentTzGuess).fromNow()
                  )}
                </span>
                <span className="d-none d-md-block">
                  Created:{" "}
                  {getReadableDate(
                    item.created,
                    moment(item.created).tz(momentTzGuess).fromNow()
                  )}
                </span>
              </div>
              <p className="mb-1 text-left">{item.subject}</p>
              <div className="lead text-left">
                <span className="">
                  <TicketStatusBadge
                    provider={item.provider}
                    status={item.status}
                  />
                </span>
                <div className="d-md-inline-block pl-lg-3 pt-1 pt-lg-0">
                  Owner:
                  <span className="pl-2">{createdBy(item.user)}</span>
                </div>
              </div>
            </button>
          );
        })}
      </>
    );
  }
}

const TicketExportButton = (props) => {
  const [gettingData, setGettingData] = useState(false);
  const [stateApp, stateAppActions] = appState();

  function getStatusValue(status) {
    if (status === "New") {
      return 1;
    } else if (status === "Open") {
      return 2;
    } else if (status === "WaitOnCust") {
      return 3;
    }
    return 4;
  }

  const exportTickets = async (statuses, filename_prefix) => {
    let tickets = await ConnectriaTicketsService.exportAllTickets();
    setGettingData(false);
    if (tickets.length === 0) return;

    // convert tickets to CSV format
    tickets = tickets.map((ticket) => {
      let created = moment(ticket.Created).format("MM/DD/YY hh:mm:ss");
      let lastUpdate = moment(ticket["Last Update On"]).format(
        "MM/DD/YY hh:mm:ss"
      );
      return {
        Id: ticket.Id,
        CustomerTicket: ticket.CustomerTicket,
        "Priority/Type": ticket.Priority + " / " + ticket.Type,
        Status:
          ticket.Status === "WaitOnCust" ? "Input Required" : ticket.Status,
        Owner: ticket.Owner,
        Team: ticket.Team,
        Subject: ticket.Subject,
        Created: created + " " + ticket["Last Update By"],
        "Last Update": created === lastUpdate ? null : lastUpdate,
        Devices: ticket.Devices,
        "Ticket Link": `${window.location.origin}/app/view/ticket/${ticket.Id}`,
      };
    });
    tickets.sort((a, b) => getStatusValue(a.Status) - getStatusValue(b.Status));

    const filename =
      stateApp.userInfo.organization_name +
      filename_prefix +
      moment().format("YYYY-MM-DD_hh-mm-ss") +
      ".csv";
    CSVUtility.downloadCSVFromJson(filename, tickets);

    stateAppActions.setAlert({
      content: <p style={{ color: "white" }}>Tickets successfully exported</p>,
      className: "success",
      visible: true,
    });
    setTimeout(() => {
      stateAppActions.setAlert((prevState) => ({
        ...prevState,
        visible: false,
      }));
    }, 2000);
  };

  return (
    <span className="ml-3 my-auto" style={{ fontSize: "15px" }}>
      {gettingData ? (
        <i className="fas fa-spinner fa-spin" id="ticket-export-loading" />
      ) : (
        <>
          <i
            className="fas fa-file-export"
            id="ticket-export-button"
            onClick={() => {
              setGettingData(true);
              exportTickets(
                [
                  "New",
                  "Open",
                  "Scheduled",
                  "WaitOnCust",
                  "Resolved",
                  "Closed",
                  "Suspended",
                  "Cancelled",
                ],
                "_All_Tickets_"
              );
            }}
          />
          <UncontrolledTooltip placement="right" target="ticket-export-button">
            Export All Tickets For 3 Months
          </UncontrolledTooltip>
        </>
      )}
    </span>
  );
};

function Inbox(props) {
  const { hasViewOnlyAccess } = props;
  //plugin state
  const [state, stateActions] = useTriaTicketsState();
  //local state
  const [offset, setOffset] = useState(0);
  const [filterState, setFilterState] = useState({});
  const [advancedFilterState, setAdvancedFilterState] = useState({});
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const source = axios.CancelToken.source();

  const [isError, setIsError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [newSearchFilter, setNewSearchFilter] = useState({ sort_by: WEIGHTED_SORT });

  const [totalItems, setTotalItems] = useState(0);

  const handleNextPage = () => {
    if (currentPage < Math.ceil(totalItems / TICKETPULLSIZE)) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const transformFilterState = (filterState) => {
    const transformedFilter = {
      searchTerm: filterState.keyword?.[0] || "",
      status: filterState?.status || "",// status: filterState.status?.[0] || "",
      ticketNumber: filterState.ticketNumber || "",
      requester_id: filterState.requester_id || "",
      portal_user_id: filterState.portal_user_id || "",
      sort_by: WEIGHTED_SORT, // TODO make this dynamic later if it needs to be
    };

    return transformedFilter;
  };

  // fetch tickets
  useAsyncEffect(
    async (isMounted) => {
      let tickets = [];
      const clearInbox = true;

      // set loading state
      stateActions.setIsLoading(true);
      if (clearInbox) {
        stateActions.clearTickets();
      }

      try {
        const formattedFilters = transformFilterState(filterState);
        const searchFilterData =
          props.stateApp.orgType === PROVIDERS.CONNECTRIA &&
            !props.stateApp.isHybrid
            ? formattedFilters
            : newSearchFilter;
        const ticketsResponse = await TicketService.getTickets(
          currentPage,
          TICKETPULLSIZE,
          searchFilterData
        );

        if (!isMounted()) return;

        tickets = ticketsResponse.data.body.tickets || [];
        tickets = tickets.filter((ticket) => !ticket.internal_use || ticket.internal_use === "FALSE");

        setTotalItems(ticketsResponse.data.body.totalTickets);
        stateActions.setTickets(tickets);

        // update tickets state
        for (var i = 0; i < tickets.length; i++) {
          stateActions.addTicket(tickets[i]);
        }
        stateActions.setIsLoading(false);

        setOffset(TICKETPULLSIZE);
      } catch (error) {
        if (axios.isCancel(error)) {
          // request cancelled
        } else {
          throw error;
        }
      } finally {
        if (isMounted()) {
          stateActions.setIsLoading(false);
        }
      }
    },
    () => {
      console.log("unmount canceled");
      source.cancel();
    },
    [currentPage, filterState, advancedFilterState, newSearchFilter]
  );

  return (
    <>
      {showAdvancedSearch && (
        <AdvancedTicketSearch
          inboxFilterState={advancedFilterState}
          setInboxFilterState={setAdvancedFilterState}
        />
      )}
      {!showAdvancedSearch && (
        <TicketFilters
          filterState={filterState}
          setFilterState={setFilterState}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          newSearchFilter={newSearchFilter}
          setNewSearchFilter={setNewSearchFilter}
          hasViewOnlyAccess={hasViewOnlyAccess}
        />
      )}
      <div className="list-group mt-1" id="connectria-db-tickets">
        <InboxItems
          tickets={state.tickets}
          stateApp={props.stateApp}
          stateAppActions={props.stateAppActions}
          statePlugin={props.statePlugin}
          statePluginActions={props.statePluginActions}
        />
      </div>

      {!state.isLoading && !isError && state.tickets.length < totalItems && (
        <PaginationControls
          currentPage={currentPage}
          itemsPerPage={TICKETPULLSIZE}
          totalItems={totalItems}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          itemsName="Tickets"
        />
      )}

      {!state.isLoading && isError && (
        <div className="text-center mt-4 text-bold">
          <i className="fas fa-exclamation-triangle"></i> Error Loading Tickets.
        </div>
      )}
      {!state.isLoading && !isError && state.tickets.length === 0 && (
        <div className="text-center mt-4 text-bold">
          No tickets found. Try adjusting your filter.
        </div>
      )}
      {state.isLoading && (
        <div className="text-center">
          <i
            className="fas fa-spinner m-3 mt-4 fa-spin"
            style={{ fontSize: "30px" }}
          ></i>
        </div>
      )}
    </>
  );
}

const ConnectriaTicketsDashboardTab = (props) => {
  const [stateApp, stateAppActions] = appState();
  const hasViewOnlyAccess = stateApp.policies.includes("VIEW_ONLY_TICKETS");

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 id="connectria-ticket-db-body-title" className="text-left">
          Ticketing Inbox
        </h3>
        {!hasViewOnlyAccess && (
          <div className="d-flex gap-2">
            <Button icon="fas fa-plus" to="/app/create-ticket" size="sm" variant="secondary">
              New Ticket
            </Button>

            {stateApp.orgType === PROVIDERS.CONNECTRIA && !stateApp.isHybrid && (
              <TicketExportButton />
            )}
          </div>
        )}
      </div>
      <Inbox
        stateApp={stateApp}
        stateAppActions={stateAppActions}
        hasViewOnlyAccess={hasViewOnlyAccess}
      />
    </>
  );
};

const TicketFilters = (props) => {
  const {
    filterState,
    setFilterState,
    currentPage,
    setCurrentPage,
    newSearchFilter,
    setNewSearchFilter,
  } = props;
  const [stateApp, stateAppActions] = appState();
  const [state, stateActions] = useTriaTicketsState();
  const [statusId, setStatusId] = useState("0");
  const [ticketNo, setTicketNo] = useState("");
  const [keyword, setKeyword] = useState("");
  const [searchName, setSearchName] = useState("");
  const [filterMine, setFilterMine] = useState(false);
  const [filterConnectriaMine, setFilterConnectriaMine] = useState(false);
  const [originalTickets, setOriginalTickets] = useState([]);
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        if (!(stateApp.orgType === PROVIDERS.CONNECTRIA) || stateApp.isHybrid) {
          const ticketsResponse = await TicketService.getTickets(
            currentPage,
            TICKETPULLSIZE,
            newSearchFilter
          );
          const tickets = ticketsResponse?.data?.body?.tickets || [];
          setOriginalTickets(tickets.filter((ticket) => !ticket.internal_use || ticket.internal_use === "FALSE"));
        }
      } catch (error) {
        console.error("Error fetching tickets:", error);
      }
    };

    fetchTickets();
  }, [currentPage, newSearchFilter]);

  useEffect(() => {
    const updateFilterState = async () => {
      try {
        const userId = await ConnectriaTicketsService.portalUserId();

        if (filterConnectriaMine) {
          setFilterState((prevState) => ({
            ...prevState,
            requester_id: stateApp.userInfo.external_id,
            portal_user_id: userId,
          }));
        } else {
          setFilterState((prevState) => ({
            ...prevState,
            requester_id: "",
            portal_user_id: "",
          }));
        }

        setCurrentPage(1);
      } catch (error) {
        console.error("Failed to update filter state:", error);
      }
    };

    updateFilterState();
    handleSearch();
  }, [filterConnectriaMine, filterMine, statusId]);

  const handleInputChange = (e) => {
    setSearchName(e.target.value);
  };

  const handleTicketNumber = (e) => {
    setTicketNo(e.target.value);
  };

  const handleFilterMine = () => {
    setFilterMine((prevState) => !prevState);
  };

  const handleSearch = async () => {
    let userId = await ConnectriaTicketsService.portalUserId();
    setNewSearchFilter((prevFilter) => {
      const newFilter = {
        searchTerm: searchName,
        ticketNumber: ticketNo,
        status: HYBRID_STATUS_OPTIONS[statusId]?.status || [],
        sort_by: WEIGHTED_SORT,
      };
      const statusName = HYBRID_STATUS_OPTIONS[statusId]?.name || '';
      // if (!statusName || ['All', 'All Open'].includes(statusName)) {
      //   // Set the sort_by for CT tickets API call in Ticketing Integration Lambda:
      //   newFilter.sort_by = WEIGHTED_SORT;
      // } else {
      //   newFilter.sort_by = "lastUpdate desc";
      // }
      // // let customerId = stateApp.organization.customer_id; // customerId is a number and this can't occur. ++ Nothing is actually using customerId
      // if (customerId?.toLowerCase().startsWith("le")) {
      //   customerId = customerId.slice(2);
      // }
      if (filterMine) {
        return {
          ...prevFilter,
          ...newFilter,
          requester_id: stateApp.userInfo.external_id,
          ...(stateApp.orgType === PROVIDERS.CONNECTRIA || stateApp.isHybrid
            ? { portal_user_id: userId }
            : {})
        };
      }

      return {
        ...newFilter
      };
    });

    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleStatusChange = (e) => {
    let newStatusId = e.target.value;
    setStatusId(statusId);
    stateActions.setStatusId(e.target.value);
    let sort_by = WEIGHTED_SORT;
    // let sort_by = "lastUpdate desc"; // created
    // if (newStatusId == '0' || newStatusId == '1') { // All and All Open
    //   sort_by = WEIGHTED_SORT;
    // }
    setCurrentPage(1);
    setFilterState({
      ...filterState,
      status: statusFilters[newStatusId] || [],
      ticketNumber: ticketNo,
      sort_by: sort_by,
    });
  };

  const handleStatusChangeHybrid = (e) => {
    let newStatusId = e.target.value;
    setStatusId(newStatusId);
  };

  const handleKeywordChange = (e, newKeyword) => {
    e.preventDefault();
    setCurrentPage(1);
    setFilterState({ ...filterState, keyword: [newKeyword] });
  };

  const handleConnectriaMyTickets = async () => {
    setFilterConnectriaMine((prevState) => !prevState);
  }

  const ticketEntry = (e) => {
    if (e) {
      e.preventDefault();
    }
    setCurrentPage(1);
    setFilterState({ ...filterState, ticketNumber: ticketNo });
  };

  const handleClearFilters = () => {
    setFilterMine(false);
    setSearchName('');
    setTicketNo('');
    setStatusId("0")
    setCurrentPage(1);
    setNewSearchFilter((prevFilter) => ({
      searchTerm: '',
      ticketNumber: '',
      filterStatus: 0,
    }));

    stateActions.setTickets(originalTickets);
  };
  if (stateApp.orgType === PROVIDERS.CONNECTRIA && !stateApp.isHybrid) {
    return (
      <div className="mkt-form-panel row mx-0">
        <div className="row col-lg-4 col-sm-6 mt-2">
          <div className="col-6">
            <ReactStrapInput
              type="select"
              name="filterStatus"
              id="filterStatus"
              onChange={handleStatusChange}
              value={state.statusId}
            >
              <option value="0">All</option>
              <option value="1">All Open</option>
              <option value="2">New</option>
              <option value="3">Open</option>
              <option value="4">Input Required</option>
              <option value="5">Scheduled</option>
              <option value="6">Resolved</option>
              <option value="7">Closed</option>
              <option value="8">Suspended</option>
              <option value="9">Cancelled</option>
            </ReactStrapInput>
          </div>
          <div className="col-6">
            <form onSubmit={(e) => ticketEntry(e)}>
              <ReactStrapInput
                onChange={(e) => setTicketNo(e.target.value)}
                type="text"
                placeholder="Ticket #"
                value={ticketNo}
              />
            </form>
          </div>
        </div>

        <div className="d-flex flex-fill col-lg-auto col-sm-12 mt-2">
          <form
            className="flex-fill"
            onSubmit={(e) => handleKeywordChange(e, keyword)}
          >
            <ReactStrapInput
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="Search Subject and Issue"
              value={keyword}
            />
          </form>
          <i
            className="fa fa-times mr-2"
            style={{
              marginLeft: -20 + "px",
              marginTop: 12 + "px",
              cursor: "pointer",
            }}
            onClick={(e) => {
              setKeyword("");
              handleKeywordChange(e, "");
            }}
          ></i>
        </div>
        <div
          className="col-lg-3 col-sm-6 row justify-content-between mt-2"
          style={{ maxWidth: "175px", marginLeft: "2px" }}
        >
          <FormGroup className="">
            <div className="position-relative form-check">
              <label className="form-check-label">
                <nobr>My Tickets</nobr>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={filterConnectriaMine}
                  onChange={handleConnectriaMyTickets}
                  onKeyUp={handleSearchKeyPress}
                />
                <span className="form-check-sign">
                  <span className="check"></span>
                </span>
              </label>
            </div>
          </FormGroup>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="mkt-form-panel">
        <div className="d-flex gap-3">
          <ReactStrapInput
            type="select"
            name="filterStatus"
            id="filterStatus"
            value={statusId}
            onChange={handleStatusChangeHybrid}
          >
            {HYBRID_STATUS_OPTIONS.map((element, index) => (
              <option key={'filterStatusOption' + index} value={index}>
                {element.name}
              </option>
            ))}
          </ReactStrapInput>
          <Input
            type="text"
            placeholder="Search by subject"
            value={searchName}
            onChange={handleInputChange}
            onKeyUp={handleSearchKeyPress}
            className="form-control"
          />
          <Input
            onChange={handleTicketNumber}
            onKeyUp={handleSearchKeyPress}
            type="text"
            placeholder="Ticket #"
            value={ticketNo}
            className="form-control"
          />
        </div>
        <div className="d-flex justify-content-between mt-3">
          <div className="d-flex gap-6">
            <div className="form-check">
              <label className="form-check-label">
                <nobr>My Tickets only</nobr>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={filterMine}
                  onChange={handleFilterMine}
                />
                <span className="form-check-sign">
                  <span className="check" />
                </span>
              </label>
            </div>
            <Button onClick={handleClearFilters} variant="ghost">
              Clear Filters
            </Button>
          </div>
          <div className="d-flex justify-center">
            <Button onClick={handleSearch} icon="fa fa-search">
              Search
            </Button>


          </div>
        </div>
      </div>
    </div>
  );
};

export { ConnectriaTicketsDashboardTab, Inbox };
